import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import JobType from "../types/jobType";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";
import { AnswerTypes } from "../types/questionVM";

interface JobTypeState {
  jobType: JobType;
  jobTypeSearchResults?: SearchResults;
  jobTypeFilter: SearchParams;
  currentViewJobType?: JobType;
  status: "idle" | "loading" | "failed";
  searchStatus: "idle" | "loading" | "failed";
  jobTypes: JobType[];
}

const initialState: JobTypeState = {
  jobType: {
    id: 0,
    name: "",
    abbrev: "",
    icon: undefined,
    color: "#000000",
    showRecentlyClosed: false,
    logHours: false,
    // TODO: Update this when we update questions
    questions: [
      {
        id: 0,
        questionText: "Status/Type",
        answerType: { id: AnswerTypes.Status, value: "" },
        required: true,
        sensitiveData: false,
        multipleChoice: false,
        freeSolo: false,
        choices: [],
        filterSelectedJobStatuses: [],
        order: 1,
        customStaticOptions: false,
        sortByAscending: false,
      },
      {
        id: 0,
        questionText: "Title",
        answerType: { id: AnswerTypes.Title, value: "" },
        required: true,
        sensitiveData: false,
        multipleChoice: false,
        freeSolo: false,
        choices: [],
        filterSelectedJobStatuses: [],
        order: 2,
        customStaticOptions: false,
        sortByAscending: false,
      },
      // {
      //   id: 0,
      //   jobTypeId: 0,
      //   order: 3,
      //   question: {
      //     id: 0,
      //     questionText: "Weekly Task",
      //     answerType: { id: AnswerTypes.WeeklyTask, value: "" },
      //     required: false,
      //     sensitiveData: false,
      //     choices: [],
      //   },
      // },
      {
        id: 0,
        questionText: "Description",
        answerType: { id: AnswerTypes.Multiline, value: "" },
        required: true,
        sensitiveData: false,
        multipleChoice: false,
        freeSolo: false,
        choices: [],
        filterSelectedJobStatuses: [],
        order: 3,
        customStaticOptions: false,
        sortByAscending: false,
      },
    ],
    statuses: [],
  },
  jobTypeFilter: defaultParams,
  jobTypeSearchResults: undefined,
  jobTypes: [],
  status: "idle",
  searchStatus: "idle",
};

export const getJobTypeId = createAsyncThunk<JobType, number>(
  "job-type/get/id",
  async (id: number) => {
    const response = await http.get<JobType>(`/jobtype/${id}`);
    return response.data;
  }
);

export const editJobType = createAsyncThunk<JobType, JobType>(
  "job-type/edit",
  async (jobType: JobType) => {
    const response = await http.put<JobType>(
      `/jobtype/${jobType.id}`,
      jobType,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const deleteJobType = createAsyncThunk<JobType, number>(
  "job-type/delete",
  async (id: number) => {
    const response = await http.delete<JobType>(`/jobtype/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const postJobType = createAsyncThunk<JobType, JobType>(
  "job-type/post",
  async (jobType: JobType) => {
    const response = await http.post<JobType>(`/jobtype`, jobType, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchJobTypes = createAsyncThunk<SearchResults, SearchParams>(
  "job-type/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`jobtype/search?${qs}`);
    return response.data;
  }
);

export const getJobTypes = createAsyncThunk<JobType[]>(
  "job-type/get",
  async () => {
    const response = await http.get<JobType[]>(`jobtype`);
    return response.data;
  }
);

export const reorderJobTypes = createAsyncThunk<null, JobType[]>(
  "job-type/reorder",
  async (types: JobType[]) => {
    const response = await http.patch(`jobtype/reorder`, types, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

const jobTypeSlice = createSlice({
  name: "jobType",
  initialState,
  reducers: {
    resetJobType(state) {
      state.jobType = { ...initialState.jobType };
    },
    setJobTypeFilter(state, action) {
      state.jobTypeFilter = action.payload;
    },
    setCurrentViewJobType(state, action) {
      state.currentViewJobType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getJobTypeId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getJobTypeId.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobType = action.payload;
      })
      .addCase(getJobTypeId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editJobType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editJobType.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(editJobType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteJobType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteJobType.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(deleteJobType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postJobType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postJobType.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(postJobType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchJobTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchJobTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.jobTypeSearchResults = action.payload;
      })
      .addCase(searchJobTypes.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getJobTypes.pending, (state) => {
        state.searchStatus = "loading";
      })
      .addCase(getJobTypes.fulfilled, (state, action) => {
        state.searchStatus = "idle";
        state.jobTypes = action.payload;
      })
      .addCase(getJobTypes.rejected, (state) => {
        state.searchStatus = "failed";
      })
      .addCase(reorderJobTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(reorderJobTypes.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(reorderJobTypes.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default jobTypeSlice.reducer;
export const { resetJobType, setJobTypeFilter, setCurrentViewJobType } =
  jobTypeSlice.actions;
